import {
  Platform,
  ResponseEnum,
  type ShopSummaryRequest,
  type ShopSummaryResponseV4,
  type ShopSummaryResponseV4Failure,
} from "@b2bportal/air-shopping-api";
import { TripCategory } from "@overrides/types";
import { flightsShoppingApi, getDeviceType } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { selectShowAllAirCanadaPorterFares } from "@tenant/experiments";
import type { FlightState } from "../../../types";
import {
  filterLowestFaresForFixedPricing,
  transformFlightsToFourFareClasses,
} from "../utils";

/** `ErrorCode.code` values from `ShopSummaryResponseV4Failure.errors` */
type ShopSummaryV4FailureErrorCode =
  ShopSummaryResponseV4Failure["errors"][number]["code"];

export type FetchFlightsRejectValue = {
  error: string;
  shopSummaryErrorCodes?: ReadonlyArray<ShopSummaryV4FailureErrorCode>;
};

// RBC requires separate fetchFlights thunk for fixed points pricing flag and fare class filtering
export const fetchFlights = createAsyncThunk<
  ShopSummaryResponseV4,
  { searchFixedPointsFares?: boolean; isFixedPointsPricingEligible?: boolean },
  { state: FlightState; rejectValue: FetchFlightsRejectValue }
>(
  "flightsShop/fetchFlights",
  async ({ searchFixedPointsFares = false }, thunkAPI) => {
    const state = thunkAPI.getState();

    const {
      passengerCounts: {
        adultsCount,
        infantsInSeatCount,
        infantsOnLapCount,
        childrenCount,
      },
      origin,
      destination,
      departureDate,
      returnDate,
      tripCategory,
      tripFilter,
    } = state.flights.flightSearch;

    const { isDesktopAndUp } = getDeviceType();
    try {
      if (origin == null || destination == null || departureDate == null) {
        return thunkAPI.rejectWithValue({
          error: "Missing required fields",
        });
      }
      const requestBody: ShopSummaryRequest = {
        passengers: {
          ADT: Number(adultsCount),
          ...(infantsInSeatCount ? { INS: Number(infantsInSeatCount) } : {}),
          ...(infantsOnLapCount ? { INF: Number(infantsOnLapCount) } : {}),
          ...(childrenCount ? { CNN: Number(childrenCount) } : {}),
        },
        route: {
          origin: origin.id.code,
          destination: destination.id.code,
        },
        tripFilter,
        platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
        departureDate,
        searchFixedPointsFares,
        ...(tripCategory === TripCategory.ROUND_TRIP
          ? { returnDate: returnDate }
          : {}),
      };

      const res = await flightsShoppingApi.apiV4ShopSummaryPost(requestBody);

      const data = res.data;

      switch (data.Response) {
        case ResponseEnum.Success: {
          const fourFaresFlights = transformFlightsToFourFareClasses(
            data.value.flights
          );

          if (searchFixedPointsFares) {
            const showAllAirCanadaPorterFares =
              selectShowAllAirCanadaPorterFares(thunkAPI.getState());
            const filteredFlights = filterLowestFaresForFixedPricing(
              fourFaresFlights,
              showAllAirCanadaPorterFares
            );
            return {
              ...data.value,
              flights: filteredFlights,
            };
          }
          return {
            ...data.value,
            flights: fourFaresFlights,
          };
        }
        case ResponseEnum.Failure: {
          const shopSummaryErrorCodes = data.errors?.map((e) => e.code) ?? [];
          return thunkAPI.rejectWithValue({
            error: data.errors?.map((e) => e.code).join(", ") ?? "Failure",
            shopSummaryErrorCodes,
          });
        }
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: String(error) });
    }
  }
);
